import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';

import { AgencyStateService } from './agency-state.service';
import { PermissionsService } from '../permissions/permissions.service';
import { AuthService } from '../auth/auth.service';
import { AuthUtils } from '../auth/auth.utils';
import { FetchShops } from '../../_state/shop/shop.state';

/**
 * dashboardly-rucz3 Phase-B+: authenticated-bootstrap preload coordinator.
 *
 * Production traces showed the VARRIALE dashboard cards were gated by a SERIAL
 * cold route-guard chain — `agencyOnboardingGuard` (`/users/me`) → `permissionsGuard`
 * (`/users/permissions`) → `FetchShops` (`/merchant/get-authorized-shops`) → cards —
 * adding ~1.3s before the cards could even request data.
 *
 * This coordinator starts all three requests CONCURRENTLY at app init (wired as a
 * fire-and-forget `APP_INITIALIZER` that returns void, so it never delays bootstrap).
 * Each call primes the SAME cache / in-flight handle the guards already read:
 *   - `/users/me`            → AgencyStateService._initPromise (Promise dedup)
 *   - `/users/permissions`   → PermissionsService.cachedPermissions$ (shareReplay + 30s TTL)
 *   - `/merchant/get-authorized-shops` → ShopState._fetchShops$ in-flight dedup
 * so the guards resolve from a warm/in-flight result with ZERO duplicate HTTP calls,
 * and the serial chain collapses to ~max(single request) instead of the sum.
 *
 * It changes WHEN the requests start, never WHAT any guard decides — auth,
 * collaborator permissions, agency onboarding, demo routing, active shop, and
 * timezone/currency are all unchanged. It no-ops on unauthenticated, expired-token,
 * and demo sessions (those are owned by authGuard / DemoRoutingService).
 */
@Injectable({ providedIn: 'root' })
export class BootstrapPreloadService {
  private readonly agencyState = inject(AgencyStateService);
  private readonly permissions = inject(PermissionsService);
  private readonly auth = inject(AuthService);
  private readonly store = inject(Store);

  /**
   * Kick off the authenticated boot requests in parallel. Fire-and-forget: returns
   * void and swallows per-request errors so a slow/failed request can never reject
   * the APP_INITIALIZER or brick routing — the owning guard still performs its normal
   * (now warm) read and its normal failure/redirect path.
   */
  preload(): void {
    if (this.shouldSkip()) {
      return;
    }

    // /users/me — Promise-deduped via AgencyStateService._initPromise; the agency
    // onboarding / default-redirect guards await this same promise.
    void this.agencyState.fetchAndUpdate();

    // /users/permissions — cold shareReplay(1) observable, so we MUST subscribe to
    // fire the HTTP; permissionsGuard later shares the same cached$ (30s TTL).
    this.permissions.fetchPermissions().subscribe({ error: () => undefined });

    // /merchant/get-authorized-shops — ShopState in-flight guard shares one HTTP with
    // any concurrent guard/component dispatch (account-dropdown, agency-shop-context).
    this.store.dispatch(new FetchShops()).subscribe({ error: () => undefined });
  }

  /**
   * Synchronous (localStorage-only) gate — never subscribes to authGuard.check(),
   * whose refresh branch is async and would re-serialize the very chain we are
   * parallelizing. Skip preload unless the user is a real, authenticated,
   * non-demo session with a live token.
   */
  private shouldSkip(): boolean {
    const token = this.auth.accessToken;
    // Unauthenticated (login / sign-up / public / reset / auth-success carry no token).
    if (!token) {
      return true;
    }
    // Dead token — let authGuard.refreshAccessToken() handle it; don't preload on it.
    if (AuthUtils.isTokenExpired(token)) {
      return true;
    }
    // Demo session — hydration is owned by DemoRoutingService / demo login, which set
    // demo permissions + agency state. Never fire real /users/* or FetchShops here.
    if (this.auth.isDemoMode()) {
      return true;
    }
    return false;
  }
}
